// @flow
import * as React from 'react'
import { FormattedMessage } from 'mz-intl'
import getPrice from 'mz-sdk/services/trips/getPrice'
import { wrapWithField } from '../Field'
import Button from '../../core/Button'
import Alert from '../../core/Alert'
import TextField from '../TextField/field'
import FieldHelp from '../FieldHelp'
import Form from '../Form'
import { Container, FieldWrapper, ButtonWrapper } from './elements'
import messages from './messages.intl'

// Types
import type { Input } from '../../core/BaseField'

export type Props = {
  searchId: string,
  tripId: string,
  ondemand?: boolean,
  discountAmount?: React.Node,
  label?: React.Node,
  placeholder?: React.Node,
  disabled?: boolean,
  loading?: boolean,
  input: {
    ...Input,
    value?: Value,
    onChange?: (Value) => any,
  },
  activateLabel?: React.Node,
  removeLabel?: React.Node,
}

export type Value = string

/**
 * A field to enter coupon code. It have internal state to enter and "activate"
 * entered coupon. The value of this field is successfully activated coupon code
 * string.
 * @extends React
 */
class CouponCodeField extends React.Component<Props> {
  static defaultProps = {
    input: {},
    label: <FormattedMessage message={messages.COUPON_LABEL} />,
    placeholder: <FormattedMessage message={messages.COUPON_PLACEHOLDER} />,
    activateLabel: <FormattedMessage message={messages.ACTIVATE_COUPON} />,
    removeLabel: <FormattedMessage message={messages.REMOVE_COUPON} />,
  }

  changeValue(newValue: Value) {
    const {
      input: { onChange },
    } = this.props
    onChange && onChange(newValue)
  }

  handleRemoveCoupon = () => {
    this.changeValue('')
  }

  handleSubmitCoupon = async ({ coupon }: { coupon: Value }) => {
    try {
      await getPrice({
        result_id: this.props.tripId,
        search_id: this.props.searchId,
        coupon_code: coupon,
        ondemand: !!this.props.ondemand,
      })

      this.changeValue(coupon)
      return null
    } catch (e) {
      const errorMessage = e?.response?.coupon_code?.[0]?.user_message // eslint-disable-line
      this.handleRemoveCoupon()
      return errorMessage || e.message
    }
  }

  render() {
    const { input, discountAmount } = this.props
    const isActivated = !!input.value

    return (
      <Alert color="white">
        <Form
          initValue={{ coupon: input.value }}
          onSubmit={this.handleSubmitCoupon}
          render={({ submit, meta, value }, nest) => (
            <Container>
              <FieldWrapper>
                <TextField
                  {...nest('coupon', { dependsOnValue: true })}
                  appearance="material"
                  label={this.props.label}
                  placeholder={this.props.placeholder}
                  disabled={this.props.disabled || isActivated}
                />
                {isActivated && discountAmount && !meta.error && (
                  <FieldHelp type="success">
                    <FormattedMessage
                      message={messages.ACTIVE_DISCOUNT}
                      values={{ discountAmount }}
                    />
                  </FieldHelp>
                )}
                {!!meta.error && (
                  <FieldHelp type="error">{meta.error}</FieldHelp>
                )}
              </FieldWrapper>
              <ButtonWrapper>
                {isActivated && (
                  <Button
                    appearance="outlined"
                    onClick={this.handleRemoveCoupon}
                    disabled={this.props.disabled}
                    loading={meta.loading || !!this.props.loading}
                  >
                    {this.props.removeLabel}
                  </Button>
                )}
                {!isActivated && (
                  <Button
                    onClick={submit}
                    loading={meta.loading || !!this.props.loading}
                    disabled={!!meta.error || !value.coupon}
                  >
                    {this.props.activateLabel}
                  </Button>
                )}
              </ButtonWrapper>
            </Container>
          )}
        />
      </Alert>
    )
  }
}

export default wrapWithField(CouponCodeField)
